import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowDownCircleIcon,
  ScissorsIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/outline';
import { videoApi, VideoJobsResponse, VideoTranslationJob } from '../../services/videoApi';
import { avatarApi, Avatar } from '../../services/avatarApi';
import { voiceApi, Voice } from '@/services/audioApi';
import { AudioPageLayout, HistoryPanel, TransactionDrawer, HistoryItem } from '@/components/Audio';
import { useAuth } from '@/hooks/useAuth';
import VideoTranslationProgress from '../../components/Video/VideoTranslationProgress';
import VideoTranslationWizard, { PreselectedUpload } from '@/components/Video/VideoTranslationWizard';
import StepDetailModal from '../../components/Video/StepDetailModal';

interface VideoTranslationLocationState {
  fileId?: string;
  fileName?: string;
  fileSize?: number;
}

const VideoTranslation: React.FC = () => {
  useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Passed via router state from "Translate" actions elsewhere (Dashboard's
  // quick-start CTA, the Videos page) so the wizard opens with the video
  // already selected instead of making the user re-pick it.
  const navState = location.state as VideoTranslationLocationState | null;
  const initialUpload: PreselectedUpload | undefined = useMemo(() => {
    if (!navState?.fileId) return undefined;
    return { fileId: navState.fileId, name: navState.fileName || 'Selected video', size: navState.fileSize };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navState?.fileId, navState?.fileName, navState?.fileSize]);

  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // History and drawer state
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<HistoryItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [jobDetails, setJobDetails] = useState<VideoTranslationJob | null>(null);
  const [jobDetailsLoading, setJobDetailsLoading] = useState(false);
  const [expandedDrawerStep, setExpandedDrawerStep] = useState<string | null>(null);

  const { data: avatarData } = useQuery(
    ['avatars'],
    () => avatarApi.getAvatars(),
    { staleTime: 60000 }
  );
  const allAvatars: Avatar[] = [
    ...(avatarData?.avatars?.own ?? []),
    ...(avatarData?.avatars?.library ?? []),
  ];

  const { data: jobs } = useQuery<VideoJobsResponse>(
    ['video-jobs'],
    () => videoApi.getJobs(),
    {
      refetchInterval: (data) => {
        const hasActive = data?.jobs?.some(
          (j: any) => j.status === 'processing' || j.status === 'pending'
        );
        return hasActive ? 3000 : 30000;
      },
    }
  );

  const { data: voicesData, isLoading: voicesLoading } = useQuery(['voices'], async () => {
    const response = await voiceApi.getVoices();
    return response.data;
  });

  const voicesArray: Voice[] = Array.isArray(voicesData)
    ? voicesData
    : voicesData?.voices || voicesData?.data || [];

  const handleCancelJob = async (jobId: string) => {
    try {
      await videoApi.cancelJob(jobId);
      toast.success('Job cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['video-jobs'] });
      if (currentJobId === jobId) setCurrentJobId(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to cancel job');
    }
  };

  const handleRetryJob = async (jobId: string) => {
    try {
      await videoApi.retryJob(jobId);
      toast.success('Retrying job from last completed step');
      queryClient.invalidateQueries({ queryKey: ['video-jobs'] });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to retry job');
    }
  };

  const refreshJobDetails = async (jobId: string) => {
    try {
      const res = await videoApi.getJob(jobId);
      const job = res?.job ?? res?.data?.job ?? res;
      if (job?.id) setJobDetails(job);
    } catch { /* silent */ }
  };

  // Map video jobs to HistoryItem format whenever jobs data changes
  React.useEffect(() => {
    if (jobs?.jobs) {
      const items: HistoryItem[] = jobs.jobs.map((job: VideoTranslationJob) => {
        const youtubeTitle = job.steps?.[0]?.data?.youtubeTitle;
        const youtubeUrl = job.request?.youtubeUrl || job.steps?.[0]?.data?.youtubeUrl;
        const baseTitle = youtubeTitle
          ? `YouTube: ${youtubeTitle}`
          : (job.title || (job.request?.targetLanguage ? `→ ${job.request.targetLanguage}` : 'Untitled Translation'));
        return {
          id: job.id,
          title: baseTitle,
          createdAt: job.createdAt,
          status: job.status === 'cancelled' ? 'failed' : job.status === 'awaiting_review' ? 'processing' : job.status,
          type: 'video-translation',
          metadata: {
            sourceLanguage: job.request?.sourceLanguage,
            targetLanguage: job.request?.targetLanguage,
            videoMode: job.request?.videoMode,
            outputFormat: job.request?.outputFormat,
            youtubeUrl,
            progress: `${job.progress}%`,
            finalVideoUrl: job.result?.finalVideoUrl ?? null,
          },
        };
      });
      setHistoryItems(items);
    }
  }, [jobs]);

  const handleHistoryItemClick = (item: HistoryItem) => {
    setSelectedHistoryItem(item);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => { setSelectedHistoryItem(null); setJobDetails(null); }, 300);
  };

  const generateClipsFromHistory = async (item: HistoryItem) => {
    try {
      const { url } = await videoApi.getJobShareableUrl(item.id);
      const params = new URLSearchParams({ videoS3Url: url, title: item.title, jobId: item.id });
      navigate(`/app/clips?${params.toString()}`);
    } catch {
      toast.error('Failed to get video URL for clip generation');
    }
  };

  // Fetch full job details (with steps) when drawer opens
  useEffect(() => {
    if (!isDrawerOpen || !selectedHistoryItem) { setJobDetails(null); return; }
    const cached = jobs?.jobs?.find((j: VideoTranslationJob) => j.id === selectedHistoryItem.id);
    if (cached) setJobDetails(cached);
    setJobDetailsLoading(true);
    videoApi.getJob(selectedHistoryItem.id)
      .then((res: any) => {
        const job = res?.job ?? res?.data?.job ?? res;
        if (job?.id) setJobDetails(job);
      })
      .catch(console.error)
      .finally(() => setJobDetailsLoading(false));
  }, [isDrawerOpen, selectedHistoryItem?.id]);

  // Keep polling job details while the drawer is open and a rerun/continue is
  // actively processing.
  useEffect(() => {
    if (!isDrawerOpen || !selectedHistoryItem) return;
    if (jobDetails?.status !== 'processing' && jobDetails?.status !== 'pending') return;
    const timer = setTimeout(() => refreshJobDetails(selectedHistoryItem.id), 2000);
    return () => clearTimeout(timer);
  }, [isDrawerOpen, selectedHistoryItem?.id, jobDetails]);

  const stepStatusStyle = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'processing': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'failed': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'skipped': return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
      default: return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
  };

  const stepStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'processing': return <ArrowPathIcon className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed': return <ExclamationCircleIcon className="h-4 w-4 text-red-500" />;
      default: return <ClockIcon className="h-4 w-4 text-yellow-500" />;
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return null;
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  const detailRow = (label: string, value?: string | boolean | null) => {
    if (value === undefined || value === null || value === '') return null;
    return (
      <div key={label} className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
        <span className="text-sm font-medium text-gray-900 dark:text-white text-right max-w-[60%]">
          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
        </span>
      </div>
    );
  };

  return (
    <>
      <AudioPageLayout
        title="Video Translation"
        historyMode="drawer"
        formSection={
          <VideoTranslationWizard
            avatars={allAvatars}
            voices={voicesArray}
            voicesLoading={voicesLoading}
            initialUpload={initialUpload}
            onJobStarted={(jobId) => {
              setCurrentJobId(jobId);
              queryClient.invalidateQueries({ queryKey: ['video-jobs'] });
            }}
          />
        }
        historySection={
          <HistoryPanel
            items={historyItems}
            onItemClick={handleHistoryItemClick}
            selectedItemId={selectedHistoryItem?.id}
            renderItemActions={(item) => {
              if (item.status !== 'completed' || !item.metadata?.finalVideoUrl) return null;
              return (
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() =>
                      videoApi.downloadFinalVideo(
                        item.id,
                        item.metadata!.finalVideoUrl,
                        `translated-video-${item.id}.${item.metadata?.outputFormat || 'mp4'}`
                      )
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                  >
                    <ArrowDownCircleIcon className="h-3.5 w-3.5" />
                    Download Final Video
                  </button>
                  <button
                    type="button"
                    onClick={() => generateClipsFromHistory(item)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                  >
                    <ScissorsIcon className="h-3.5 w-3.5" />
                    Generate Clips
                  </button>
                </div>
              );
            }}
          />
        }
      />

      {currentJobId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <VideoTranslationProgress
              jobId={currentJobId}
              onCancel={() => handleCancelJob(currentJobId)}
              onComplete={() => setCurrentJobId(null)}
              onClose={() => setCurrentJobId(null)}
            />
          </div>
        </div>
      )}

      <TransactionDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        item={selectedHistoryItem}
        size="vw70"
        hideMetadata
        title={selectedHistoryItem?.title}
        renderDetails={() => {
          const job = jobDetails;
          return (
            <>
              <div className="grid grid-cols-2 gap-5 flex-1 min-h-0 overflow-hidden">
                <div className="space-y-4 overflow-y-auto min-h-0 pr-1">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Request Settings</h3>
                    <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-1">
                      {detailRow('Source Language', job?.request?.sourceLanguage || 'Auto-detect')}
                      {detailRow('Target Language', job?.request?.targetLanguage)}
                      {(!job?.request?.translationProvider || job.request.translationProvider === 'sarvam') &&
                        detailRow('Narration Mode', job?.request?.narrationMode || 'formal')}
                      {detailRow('Video Mode', job?.request?.videoMode)}
                      {detailRow('Output Format', job?.request?.outputFormat)}
                      {detailRow('Video Quality', job?.request?.videoQuality)}
                      {detailRow('Lipsync Model', job?.request?.lipsyncModel)}
                      {detailRow('Enhance Audio', job?.request?.enhanceAudio)}
                      {detailRow('Preserve Emotion', job?.request?.preserveEmotion)}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Progress</h3>
                    <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{job?.currentStep || '—'}</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{job?.progress ?? 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                        <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${job?.progress ?? 0}%` }} />
                      </div>
                    </div>
                  </div>

                  {(job?.status === 'pending' || job?.status === 'processing' || job?.status === 'awaiting_review') && (
                    <div>
                      <button
                        onClick={() => { handleCancelJob(job.id); setIsDrawerOpen(false); }}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-700 dark:text-red-400 text-sm font-medium rounded-lg border border-red-200 dark:border-red-800 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" /> Cancel Job
                      </button>
                    </div>
                  )}

                  {job?.status === 'completed' && job?.result && (
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Result</h3>
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 space-y-2">
                        {job.result.finalVideoUrl && (
                          <>
                            <button
                              onClick={() => videoApi.downloadFinalVideo(job.id, job.result.finalVideoUrl, `video-translation-${job.id}.mp4`)}
                              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" /> Download Translated Video
                            </button>
                            <button
                              onClick={() => generateClipsFromHistory({ id: job.id, title: job.title || `Translation ${job.id}`, createdAt: job.createdAt, status: job.status as any, type: 'video-translation', metadata: { finalVideoUrl: job.result.finalVideoUrl } })}
                              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                              <ScissorsIcon className="h-4 w-4" /> Generate Clips
                            </button>
                          </>
                        )}
                        {job.result.duration && (
                          <p className="text-sm text-green-700 dark:text-green-400 text-center">
                            Duration: {typeof job.result.duration === 'object'
                              ? `${job.result.duration.original?.toFixed(1)}s → ${job.result.duration.translated?.toFixed(1)}s`
                              : `${job.result.duration}s`}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {job?.error && (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                      <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Error</p>
                      <p className="text-sm text-red-600 dark:text-red-300">{job.error}</p>
                    </div>
                  )}

                  {job?.status === 'failed' && (
                    <div>
                      <button
                        onClick={() => handleRetryJob(job.id)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 text-sm font-medium rounded-lg border border-indigo-200 dark:border-indigo-800 transition-colors"
                      >
                        <ArrowPathIcon className="h-4 w-4" /> Retry from Last Completed Step
                      </button>
                    </div>
                  )}

                  {jobDetailsLoading && !job && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <ArrowPathIcon className="h-3 w-3 animate-spin" /> Loading details…
                    </div>
                  )}
                </div>

                <div className="flex flex-col min-h-0 overflow-hidden">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2 flex-shrink-0">Processing Steps</h3>
                  {!job?.steps?.length ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">No step data available.</p>
                  ) : (
                    <div className="space-y-2 overflow-y-auto flex-1">
                      {[...(job.steps)].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)).map((step: any) => (
                        <div key={step.id || step.name} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/50">
                            {stepStatusIcon(step.status)}
                            <span className="flex-1 text-sm font-medium text-gray-800 dark:text-white truncate">{step.name || step.id}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${stepStatusStyle(step.status)}`}>{step.status}</span>
                            {step.duration && <span className="text-xs text-gray-400 whitespace-nowrap">{formatDuration(step.duration)}</span>}
                          </div>
                          {(step.data || step.error) && (
                            <div className="px-3 py-2 space-y-1.5 text-sm">
                              {step.error && <p className="text-red-600 dark:text-red-400 italic">{step.error}</p>}
                              {step.data?.transcribedText && <div><p className="text-sm text-gray-400 mb-0.5">Transcription</p><p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3 bg-gray-50 dark:bg-gray-700/40 rounded p-1.5">{step.data.transcribedText}</p></div>}
                              {step.data?.translatedText && <div><p className="text-sm text-gray-400 mb-0.5">Translation</p><p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3 bg-gray-50 dark:bg-gray-700/40 rounded p-1.5">{step.data.translatedText}</p></div>}
                              {step.data?.voiceName && <p className="text-gray-500 dark:text-gray-400">Voice: <span className="text-gray-700 dark:text-gray-300">{step.data.voiceName}</span></p>}
                              {step.data?.sourceLanguage && <p className="text-gray-500 dark:text-gray-400">{step.data.sourceLanguage} → {step.data.targetLanguage}</p>}
                              {(step.data?.outputAudioUrl || step.data?.outputVideoUrl) && (
                                <button
                                  onClick={() => {
                                    const url = step.data.outputVideoUrl || step.data.outputAudioUrl;
                                    const ext = step.data.outputVideoUrl ? 'mp4' : 'mp3';
                                    videoApi.downloadFile(url, `step-${step.id}.${ext}`);
                                  }}
                                  className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  <ArrowDownTrayIcon className="h-3 w-3" /> Download step output
                                </button>
                              )}
                              {step.data?.originalAudioDuration && <p className="text-gray-400">Audio duration: {step.data.originalAudioDuration.toFixed(1)}s</p>}
                            </div>
                          )}
                          {(step.startedAt || step.completedAt) && (
                            <div className="px-3 pb-1 flex gap-3 text-[10px] text-gray-400">
                              {step.startedAt && <span>Started: {new Date(step.startedAt).toLocaleTimeString()}</span>}
                              {step.completedAt && <span>Done: {new Date(step.completedAt).toLocaleTimeString()}</span>}
                            </div>
                          )}
                          {(job?.status === 'completed' || job?.status === 'failed' || job?.status === 'cancelled' || job?.status === 'awaiting_review') && (
                            <div className="px-3 pb-2 pt-1 border-t border-gray-100 dark:border-gray-700">
                              <button
                                onClick={() => setExpandedDrawerStep(step.id)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded border border-blue-200 dark:border-blue-800 transition-colors"
                              >
                                <MagnifyingGlassPlusIcon className="h-3 w-3" />
                                {step.canRerun ? 'Expand, view & rerun' : 'View step details'}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {expandedDrawerStep && job && (
                <StepDetailModal
                  jobId={job.id}
                  job={job}
                  step={job.steps.find((s: any) => s.id === expandedDrawerStep)}
                  onClose={() => setExpandedDrawerStep(null)}
                  onChanged={() => { queryClient.invalidateQueries({ queryKey: ['video-jobs'] }); refreshJobDetails(job.id); }}
                />
              )}
            </>
          );
        }}
      />
    </>
  );
};

export default VideoTranslation;
